import { type ChangeEvent, useId, useState } from 'react';

import { ToggleBarContext } from './ToggleBar.context';
import { type ToggleBarProps, type ToggleBarValue } from './ToggleBar.types';
import { getBaseHTMLProps } from '../../types';
import { combineHandlers } from '../../utils';
import { Grid, VStack } from '../layout';
import { Typography } from '../Typography';

export const ToggleBar = <T extends string | number = string>(
  props: ToggleBarProps<T>,
) => {
  const {
    children,
    size = 'medium',
    purpose = 'primary',
    label,
    onChange,
    value,
    name,
    width,
    htmlProps,
    className,
    style,
    id,
    ...rest
  } = props;

  const generatedId = useId();
  const uniqueId = id ?? `${generatedId}-ToggleBar`;

  const [groupValue, setGroupValue] = useState<ToggleBarValue>(value);

  const handleChange = combineHandlers(
    (e: ChangeEvent<HTMLInputElement>) => setGroupValue(e.target.value),
    e => onChange && onChange(e, e.target.value as T),
  );

  const labelId = label && `${uniqueId}-label`;

  return (
    <ToggleBarContext
      value={{
        purpose,
        size,
        onChange: handleChange,
        name,
        value: groupValue,
      }}
    >
      <VStack
        {...getBaseHTMLProps(id, className, style, htmlProps, rest)}
        width={width}
        gap="x0.125"
        role="radiogroup"
        aria-labelledby={labelId ?? htmlProps?.['aria-labelledby']}
      >
        {label && (
          <Typography id={labelId} as="span" typographyType="labelMedium">
            {label}
          </Typography>
        )}
        <Grid
          defaultPageLayout={false}
          gridAutoFlow="column"
          gridAutoColumns="1fr"
        >
          {children}
        </Grid>
      </VStack>
    </ToggleBarContext>
  );
};

ToggleBar.displayName = 'ToggleBar';
